import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic, Square, Calendar, Trash2, Info, Volume2, VolumeX,
  Apple, FileText, Wine, Package, CircleSlash, HelpCircle, Sparkles
} from 'lucide-react';
import { BIN_INFO, findWasteItem } from './wasteGuide';

// Configurazione grafica per ogni tipo di rifiuto
const WASTE_STYLES = {
  'Umido': {
    icon: Apple,
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500'
  },
  'Carta e Cartone': {
    icon: FileText,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500'
  },
  'Plastica e Metalli': {
    icon: Package,
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    dot: 'bg-yellow-400'
  },
  'Vetro': {
    icon: Wine,
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  'Indifferenziato': {
    icon: Trash2,
    badge: 'bg-gray-200 text-gray-700 border-gray-300',
    dot: 'bg-gray-500'
  },
  'Nessuna raccolta': {
    icon: CircleSlash,
    badge: 'bg-gray-100 text-gray-400 border-gray-200',
    dot: 'bg-gray-300'
  }
};

const WasteBadge = ({ type }) => {
  const style = WASTE_STYLES[type] || WASTE_STYLES['Nessuna raccolta'];
  const IconComp = style.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border ${style.badge}`}>
      <IconComp className="h-3.5 w-3.5" aria-hidden="true" />
      {type}
    </span>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white/90 backdrop-blur rounded-2xl shadow-lg shadow-emerald-900/5 border border-white/60 ${className}`}>{children}</div>
);

const CardHeader = ({ children }) => (
  <div className="px-5 py-4 border-b border-gray-100">{children}</div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-800 ${className}`}>{children}</h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-5 py-4 ${className}`}>{children}</div>
);

// Selezione della migliore voce italiana disponibile
const pickItalianVoice = () => {
  const voices = window.speechSynthesis.getVoices();
  const preferred = [
    'Google italiano',
    'Microsoft Elsa - Italian (Italy)',
    'Microsoft Isabella - Italian (Italy)',
    'Alice'
  ];
  for (const name of preferred) {
    const v = voices.find(voice => voice.name === name);
    if (v) return v;
  }
  return voices.find(voice => voice.lang && voice.lang.toLowerCase().startsWith('it')) || null;
};

// Audio corrente della voce neurale (Paola)
let currentAudio = null;

const stopAllSpeech = () => {
  if (currentAudio) {
    // Stacca gli handler PRIMA di fermare l'audio: svuotare src scatena
    // onerror, che altrimenti farebbe ripartire il fallback vocale
    // proprio mentre l'utente sta per parlare
    currentAudio.onplay = null;
    currentAudio.onended = null;
    currentAudio.onerror = null;
    currentAudio.pause();
    currentAudio.removeAttribute('src');
    currentAudio.load();
    currentAudio = null;
  }
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

// Fallback: sintesi vocale del browser
const speakWithBrowser = (text, { onStart, onEnd } = {}) => {
  if (!('speechSynthesis' in window)) {
    if (onEnd) onEnd();
    return;
  }
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'it-IT';
  utterance.rate = 0.95;
  utterance.pitch = 1;

  const voice = pickItalianVoice();
  if (voice) utterance.voice = voice;

  utterance.onstart = () => { if (onStart) onStart(); };
  utterance.onend = () => { if (onEnd) onEnd(); };
  utterance.onerror = () => { if (onEnd) onEnd(); };

  window.speechSynthesis.speak(utterance);
};

// Voce principale: Paola (Piper TTS, neurale) servita dal backend, con fallback al browser.
// tone: 'calda' (default), 'allegra', 'calma', 'neutra'
const speak = (text, { onStart, onEnd, tone = 'calda' } = {}) => {
  stopAllSpeech();

  // Pulisce il testo per una pronuncia più naturale
  const cleanText = text.replace(/\n/g, '. ').replace(/[()]/g, ', ');

  const audio = new Audio(`/api/tts.php?text=${encodeURIComponent(cleanText)}&tone=${tone}`);
  currentAudio = audio;

  audio.onplay = () => { if (onStart) onStart(); };
  audio.onended = () => {
    if (currentAudio === audio) currentAudio = null;
    if (onEnd) onEnd();
  };
  audio.onerror = () => {
    // Ignora l'errore se l'audio è stato fermato volontariamente
    if (currentAudio !== audio) return;
    currentAudio = null;
    // La voce neurale non è raggiungibile (es. offline): usa la voce del browser
    speakWithBrowser(cleanText, { onStart, onEnd });
  };

  audio.play().catch(() => {
    // Ignora l'errore se l'audio è stato fermato volontariamente
    if (currentAudio !== audio) return;
    currentAudio = null;
    speakWithBrowser(cleanText, { onStart, onEnd });
  });
};

const WasteAssistant = () => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);
  const handleVoiceCommandRef = useRef(null);
  const micStreamRef = useRef(null);
  const listenTimeoutRef = useRef(null);

  // Verifica il supporto al riconoscimento vocale
  useEffect(() => {
    if (!(window.SpeechRecognition || window.webkitSpeechRecognition)) {
      setSpeechSupported(false);
    }
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      releaseMic();
    };
  }, []);

  // Rilascia il microfono acquisito con getUserMedia
  const releaseMic = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
  };

  // Safari (WebKit) riattiva la sessione audio del microfono solo se si
  // acquisisce esplicitamente uno stream con getUserMedia prima di ogni
  // ascolto: senza questo, il riconoscimento funziona solo la prima volta
  const acquireMic = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return true; // browser senza getUserMedia: lascia fare a SpeechRecognition
    }
    try {
      releaseMic();
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (e) {
      setResponse('Per usare l\'assistente vocale devi consentire l\'accesso al microfono nelle impostazioni del browser.');
      return false;
    }
  };

  // Crea una NUOVA istanza a ogni ascolto: riutilizzare la stessa istanza
  // causa il blocco del riconoscimento dopo il primo uso (Chrome/Android)
  const createRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = 'it-IT';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      if (interim) setInterimTranscript(interim);
      if (final) {
        const text = final.toLowerCase().trim();
        setTranscript(text);
        setInterimTranscript('');
        if (handleVoiceCommandRef.current) handleVoiceCommandRef.current(text);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      setInterimTranscript('');
      if (event.error === 'not-allowed') {
        setResponse('Per usare l\'assistente vocale devi consentire l\'accesso al microfono nelle impostazioni del browser.');
      } else if (event.error === 'no-speech') {
        setResponse('Non ho sentito nulla. Tocca il microfono e riprova.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      if (listenTimeoutRef.current) {
        clearTimeout(listenTimeoutRef.current);
        listenTimeoutRef.current = null;
      }
      releaseMic();
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
    };

    return recognition;
  }, []);

  // Precaricamento delle voci per la sintesi vocale
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
  }, []);

  // Statistiche anonime: registra la visita
  useEffect(() => {
    fetch('/api/track.php?e=visit').catch(() => {});
  }, []);

  const stopSpeaking = useCallback(() => {
    stopAllSpeech();
    setIsSpeaking(false);
  }, []);

  const getNthDayOfMonth = (n, dayOfWeek, date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    while (firstDay.getDay() !== dayOfWeek) {
      firstDay.setDate(firstDay.getDate() + 1);
    }
    firstDay.setDate(firstDay.getDate() + (n - 1) * 7);
    return firstDay;
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() && 
           date1.getMonth() === date2.getMonth() && 
           date1.getFullYear() === date2.getFullYear();
  };

  const getNextCollectionDates = (wasteType, startDate = new Date(), numDates = 3) => {
    const dates = [];
    const currentDate = new Date(startDate);
    
    // Normalizza il tipo di rifiuto per la ricerca
    const normalizedWasteType = wasteType.toLowerCase();
    
    while (dates.length < numDates) {
      const schedule = getDaySchedule(currentDate);
      if (schedule.type.some(type => type.toLowerCase().includes(normalizedWasteType))) {
        dates.push({
          date: new Date(currentDate),
          schedule
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Limite di sicurezza per evitare loop infiniti
      if (dates.length === 0 && currentDate > new Date(startDate.getTime() + 31 * 24 * 60 * 60 * 1000)) {
        return null; // Nessuna data trovata nel prossimo mese
      }
    }
    
    return dates;
  };

  const getDaySchedule = (date) => {
    const dayName = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'][date.getDay()];
    
    // Calcola i giorni speciali del mese
    const secondMonday = getNthDayOfMonth(2, 1, date);
    const fourthMonday = getNthDayOfMonth(4, 1, date);
    const secondWednesday = getNthDayOfMonth(2, 3, date);
    const fourthWednesday = getNthDayOfMonth(4, 3, date);
    const secondFriday = getNthDayOfMonth(2, 5, date);
    const fourthFriday = getNthDayOfMonth(4, 5, date);

        const schedules = {
      'lunedì': () => {
        const isSecondOrFourthMonday = isSameDay(date, secondMonday) || isSameDay(date, fourthMonday);
        return {
          type: ['Umido'].concat(isSecondOrFourthMonday ? ['Carta e Cartone'] : []),
          time: `8:00-12:00 per Umido${isSecondOrFourthMonday ? ', 12:00-18:00 per Carta' : ''}`
        };
      },
      'martedì': () => ({
        type: ['Nessuna raccolta'],
        time: 'N/A'
      }),
      'mercoledì': () => {
        const isSecondOrFourthWednesday = isSameDay(date, secondWednesday) || isSameDay(date, fourthWednesday);
        return {
          type: isSecondOrFourthWednesday ? ['Plastica e Metalli'] : ['Nessuna raccolta'],
          time: isSecondOrFourthWednesday ? '12:00-18:00' : 'N/A'
        };
      },
      'giovedì': () => ({
        type: ['Nessuna raccolta'],
        time: 'N/A'
      }),
      'venerdì': () => {
        const isSecondOrFourthFriday = isSameDay(date, secondFriday) || isSameDay(date, fourthFriday);
        return {
          type: ['Umido'].concat(isSecondOrFourthFriday ? ['Vetro'] : []),
          time: `8:00-12:00 per Umido${isSecondOrFourthFriday ? ', 12:00-18:00 per Vetro' : ''}`
        };
      },
      'sabato': () => ({
        type: ['Indifferenziato'],
        time: '06:00-09:00'
      })
    };


    return schedules[dayName] ? schedules[dayName]() : { type: ['Nessuna raccolta'], time: 'N/A' };
  };

  // Piccolo aiuto per dare carattere alla voce: sceglie una variante a caso
  const pick = (options) => options[Math.floor(Math.random() * options.length)];

  const describeSchedule = (schedule) => {
    if (schedule.type.length === 1 && schedule.type[0] === 'Nessuna raccolta') {
      return pick([
        'non è prevista nessuna raccolta. Giornata libera!',
        'non c’è nessuna raccolta in programma. Puoi rilassarti!',
        'i bidoni riposano: nessuna raccolta prevista.'
      ]);
    }
    return `si raccoglie ${schedule.type.join(' e ')}. Orario: ${schedule.time}`;
  };

  const handleVoiceCommand = (command) => {
    const today = new Date();
    const days = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

    const wasteTypes = {
      'umido': 'Umido',
      'organico': 'Umido',
      'carta': 'Carta',
      'cartone': 'Carta',
      'vetro': 'Vetro',
      'plastica': 'Plastica',
      'metalli': 'Metalli',
      'metallo': 'Metalli',
      'lattine': 'Metalli',
      'indifferenziato': 'Indifferenziato',
      'secco': 'Indifferenziato'
    };

    const whenMatch = command.includes('quando') || command.includes('prossim');
    const wasteMatch = Object.keys(wasteTypes).find(type => command.includes(type));
    const dayMatch = days.find(day =>
      command.includes(day) || command.includes(day.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))
    );

    // "Dove si butta X?" - cerca l'oggetto nella guida dei rifiuti
    const itemEntry = findWasteItem(command);
    const whereMatch = /dove|in che|in quale|che bidone|quale bidone|si butta|butto|buttare|getto|gettare|smaltis|smaltire/.test(command);

    // Costruisce la risposta per un oggetto della guida: categoria + prossima raccolta
    const answerForItem = (entry) => {
      if (entry.advice) {
        return `${entry.item} ${entry.verb} ${entry.advice}`;
      }
      const info = BIN_INFO[entry.bin];
      let txt = `${entry.item} ${entry.verb} ${info.phrase}`;
      if (entry.note) txt += ` (${entry.note})`;
      const nextDates = getNextCollectionDates(info.search, today, 1);
      if (nextDates && nextDates.length > 0) {
        const { date, schedule } = nextDates[0];
        const dateStr = date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        txt += `. La prossima raccolta di ${info.label} è ${dateStr}, orario ${schedule.time}.`;
      } else {
        txt += `. Non ho trovato raccolte di ${info.label} programmate nel prossimo mese.`;
      }
      return txt;
    };

    let responseText = '';
    let tone = 'calda';

    if (itemEntry && whereMatch && !(whenMatch && wasteMatch)) {
      responseText = answerForItem(itemEntry);
    } else if (whenMatch && wasteMatch) {
      const wasteType = wasteTypes[wasteMatch];
      const nextDates = getNextCollectionDates(wasteType);

      if (nextDates) {
        const dateStrings = nextDates.map(({ date, schedule }) => {
          const dateStr = date.toLocaleDateString('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          });
          return `${dateStr} (${schedule.time})`;
        });

        const opener = pick([
          'Ecco qui!',
          'Trovato!',
          'Certo!',
          'Subito!'
        ]);
        responseText = `${opener} Le prossime date per la raccolta di ${wasteType} sono:\n${dateStrings.join('\n')}`;
      } else {
        responseText = `Mi dispiace, non ho trovato date programmate per la raccolta di ${wasteType} nel prossimo mese.`;
      }
    } else if (command.includes('dopodomani') || command.includes('dopo domani')) {
      const dayAfter = new Date(today);
      dayAfter.setDate(dayAfter.getDate() + 2);
      const schedule = getDaySchedule(dayAfter);
      responseText = `${pick(['Allora,', 'Vediamo…', 'Dunque,'])} dopodomani è ${days[dayAfter.getDay()]} e ${describeSchedule(schedule)}`;
    } else if (command.includes('oggi')) {
      const schedule = getDaySchedule(today);
      responseText = `${pick(['Allora,', 'Vediamo…', 'Dunque,'])} oggi è ${days[today.getDay()]} e ${describeSchedule(schedule)}`;
    } else if (command.includes('domani')) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const schedule = getDaySchedule(tomorrow);
      responseText = `${pick(['Allora,', 'Vediamo…', 'Dunque,'])} domani è ${days[tomorrow.getDay()]} e ${describeSchedule(schedule)}`;
    } else if (dayMatch) {
      const targetDay = days.indexOf(dayMatch);
      const target = new Date(today);
      let diff = (targetDay - today.getDay() + 7) % 7;
      if (diff === 0) diff = 7;
      target.setDate(target.getDate() + diff);
      const schedule = getDaySchedule(target);
      const dateStr = target.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
      responseText = `${dayMatch.charAt(0).toUpperCase() + dayMatch.slice(1)} ${dateStr} ${describeSchedule(schedule)}`;
    } else if (command.includes('calendario')) {
      responseText = 'Puoi consultare il calendario completo della raccolta differenziata qui sotto.';
    } else if (itemEntry) {
      // L'utente ha nominato solo l'oggetto, es. "guscio delle vongole"
      responseText = answerForItem(itemEntry);
    } else if (command.includes('ciao') || command.includes('buongiorno') || command.includes('buonasera')) {
      tone = 'allegra';
      responseText = pick([
        'Ciao! Sono Diffy, il tuo assistente per la raccolta differenziata. Chiedimi pure cosa si butta oggi!',
        'Ciao! Come posso aiutarti con la raccolta differenziata?',
        'Buongiorno! Vuoi sapere cosa si raccoglie oggi a Savignano?'
      ]);
    } else if (command.includes('grazie')) {
      tone = 'allegra';
      responseText = pick([
        'Di niente! Sono qui per questo.',
        'Prego! Differenziare bene è un gesto d’amore per il paese.',
        'Figurati! A presto!'
      ]);
    } else if (command.includes('aiuto') || command.includes('cosa puoi fare') || command.includes('come funzioni')) {
      responseText = 'Puoi chiedermi: cosa si butta oggi o domani, quando viene raccolto un tipo di rifiuto, oppure dove si butta un oggetto, ad esempio: dove si butta il guscio delle vongole?';
    } else if (whereMatch) {
      responseText = 'Non conosco ancora questo oggetto. In caso di dubbio: se è un imballaggio guarda i simboli sulla confezione, altrimenti mettilo nell\'indifferenziato oppure chiedi al centro di raccolta comunale.';
    } else {
      responseText = pick([
        'Mmm, non ho capito bene. Prova a chiedere "Cosa si butta oggi?" oppure "Dove si butta il polistirolo?"',
        'Scusa, questa mi è sfuggita! Puoi chiedermi ad esempio "Dove si buttano i gusci delle vongole?"',
        'Non ho afferrato. Prova con "Quando è la prossima raccolta del vetro?" o "Dove si buttano le pile?"'
      ]);
    }

    setResponse(responseText);
    fetch('/api/track.php?e=voice').catch(() => {});
    speak(responseText, {
      tone,
      onStart: () => setIsSpeaking(true),
      onEnd: () => setIsSpeaking(false)
    });
  };

  handleVoiceCommandRef.current = handleVoiceCommand;

  const toggleListening = async () => {
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    stopSpeaking();
    setTranscript('');

    // Scarta l'eventuale istanza precedente e ripartiamo da zero
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    // Riattiva la sessione audio del microfono (necessario per Safari)
    const micOk = await acquireMic();
    if (!micOk) return;

    const recognition = createRecognition();
    if (!recognition) {
      releaseMic();
      return;
    }
    recognitionRef.current = recognition;

    try {
      recognition.start();
      // Timeout di sicurezza: Safari a volte non emette onend
      listenTimeoutRef.current = setTimeout(() => {
        if (recognitionRef.current === recognition) {
          recognition.stop();
        }
      }, 12000);
    } catch (e) {
      recognitionRef.current = null;
      releaseMic();
      setIsListening(false);
    }
  };

  const getCalendarData = () => {
  // Ottieni oggi
  const today = new Date();
  
  // Trova l'inizio della settimana (lunedì)
  const monday = new Date(today);
  monday.setDate(monday.getDate() - monday.getDay() + (monday.getDay() === 0 ? -6 : 1));

  // Crea un array dei giorni della settimana con le loro date
  return ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'].map((day, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    
    return {
      day,
      date: date.toLocaleDateString('it-IT', { 
        weekday: 'long',
        day: 'numeric',
        month: 'long' 
      }),
      schedule: getDaySchedule(date),
      isToday: isSameDay(date, today)
    };
  });
};

  const todaySchedule = getDaySchedule(new Date());

  const exampleCommands = [
    '"Cosa si butta oggi?"',
    '"Cosa si butta domani?"',
    '"Quando posso buttare la carta?"',
    '"Quando è la prossima raccolta del vetro?"',
    '"Dove si butta il guscio delle vongole?"',
    '"Dove si buttano le pile?"'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 pb-[env(safe-area-inset-bottom)]">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white pt-[max(1rem,env(safe-area-inset-top))] pb-16 px-4 shadow-lg">
        <div className="container mx-auto max-w-3xl pt-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-2xl p-2.5">
              <Trash2 className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">Diffy</h1>
              <p className="text-emerald-100 text-sm sm:text-base">Raccolta differenziata · Savignano Irpino</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 -mt-10 space-y-4">
        {/* Card assistente vocale */}
        <Card>
          <CardContent className="py-6">
            <div className="flex flex-col items-center gap-4">
              {speechSupported ? (
                <>
                  <div className="relative">
                    {isListening && (
                      <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-60" aria-hidden="true" />
                    )}
                    <button
                      onClick={toggleListening}
                      aria-label={isListening ? 'Ferma ascolto' : 'Avvia assistente vocale'}
                      className={`relative p-6 rounded-full text-white shadow-xl transition-all duration-300 active:scale-95 focus:outline-none focus:ring-4 ${
                        isListening
                          ? 'bg-red-500 focus:ring-red-200 shadow-red-500/40'
                          : 'bg-gradient-to-br from-emerald-500 to-teal-600 focus:ring-emerald-200 shadow-emerald-500/40 hover:scale-105'
                      }`}
                    >
                      {isListening ? (
                        <Square className="h-9 w-9" aria-hidden="true" />
                      ) : (
                        <Mic className="h-9 w-9" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  <p className="text-gray-600 font-medium text-center" aria-live="polite">
                    {isListening
                      ? (interimTranscript ? `"${interimTranscript}"` : 'Sto ascoltando…')
                      : 'Tocca il microfono e fai una domanda'}
                  </p>
                  {transcript && !isListening && (
                    <p className="text-sm text-gray-400 italic text-center">Hai detto: "{transcript}"</p>
                  )}
                </>
              ) : (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-center">
                  Il riconoscimento vocale non è supportato da questo browser. Prova con Chrome o Safari.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Risposta */}
        {response && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 shadow-sm" role="status" aria-live="polite">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="bg-emerald-100 rounded-full p-2 shrink-0">
                  <Info className="h-4 w-4 text-emerald-700" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="text-emerald-900 font-semibold text-sm">Risposta</h4>
                  <p className="text-emerald-800 mt-1 text-sm sm:text-base" style={{ whiteSpace: 'pre-line' }}>{response}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (isSpeaking) {
                    stopSpeaking();
                  } else {
                    speak(response, {
                      onStart: () => setIsSpeaking(true),
                      onEnd: () => setIsSpeaking(false)
                    });
                  }
                }}
                aria-label={isSpeaking ? 'Interrompi lettura' : 'Leggi la risposta'}
                className="shrink-0 p-2 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                {isSpeaking ? <VolumeX className="h-5 w-5" aria-hidden="true" /> : <Volume2 className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        )}

        {/* Oggi in evidenza */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Oggi si raccoglie</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {todaySchedule.type.map(type => <WasteBadge key={type} type={type} />)}
            </div>
            {todaySchedule.time !== 'N/A' && (
              <p className="text-sm text-gray-500 mt-2">Orario: {todaySchedule.time}</p>
            )}
          </CardContent>
        </Card>

        {/* Calendario settimanale */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-600" aria-hidden="true" />
              Calendario Settimanale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {getCalendarData().map(({ day, date, schedule, isToday }) => (
                <div
                  key={day}
                  className={`p-3 rounded-xl border transition-colors ${
                    isToday
                      ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                      : 'bg-white border-gray-100 hover:border-emerald-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold capitalize text-gray-800 text-sm">{date}</p>
                    {isToday && (
                      <span className="text-[11px] font-bold text-white bg-emerald-500 rounded-full px-2 py-0.5">OGGI</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    {schedule.type.map(type => <WasteBadge key={type} type={type} />)}
                  </div>
                  {schedule.time !== 'N/A' && (
                    <p className="text-xs text-gray-500 mt-1.5">Orario: {schedule.time}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Guida */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-emerald-600" aria-hidden="true" />
              Cosa puoi chiedermi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {exampleCommands.map(cmd => (
                <div key={cmd} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                  <Mic className="h-3.5 w-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
                  {cmd}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default WasteAssistant;